import { UserIcon } from '@app/components/users/UserIcon';
import { ResourceViewPage } from '@app/core/crud/ResourceViewPage';
import { UserWithGroup } from '@app/core/types/users';
import { deleteUser } from '@app/modules/admin/api';
import { UserActionPermissionsView } from '@app/modules/admin/components/actions-permissions/UserActionPermissionsView';
import { capitalizeFirst, UserRole } from '@mi-front/core';
import { ParamDrivenTabsMui } from '@mi-front/ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { getAppUserDisplayName } from '../../../../../helpers/user';
import { UsersBreadcrumbs } from '../components/UsersBreadcrumbs';
import { useApiUser } from '../hooks/useApiUser';
import { UserInfo } from './tabs/UserInfo';
import { UserPermissionsManager } from './tabs/UserPermissionsManager';

export enum TabKey {
  INFO = 'info',
  PERMISSIONS = 'permissions',
  ACTIONS_PERMISSIONS = 'actions-permissions',
}

export function AdminUsersViewPage() {
  const { id } = useParams<{ id: string }>();
  const { formatMessage, $t } = useIntl();
  const userQuery = useApiUser(id, {
    contain: 'group',
  });

  return (
    <ResourceViewPage<UserWithGroup>
      query={userQuery}
      dataCy="users-view-screen"
      htmlTitle={(user) =>
        `${capitalizeFirst(
          $t({
            id: 'common.entities.user',
            defaultMessage: 'usuario',
          }),
        )} ${getAppUserDisplayName(user)}`
      }
      header={{
        icon: <UserIcon />,
        title: (user) => getAppUserDisplayName(user),
        breadcrumbs: () => <UsersBreadcrumbs />,
      }}
      editConfig={{
        to: (resource) => `/admin/users/${resource.id}/edit`,
      }}
      deleteConfig={{
        onDelete: (resource) => deleteUser(resource.id),
        title: () =>
          formatMessage({
            id: 'admin.users.deleteScreen.title',
            defaultMessage: 'Eliminar usuario',
          }),
        message: (resource) => (
          <FormattedMessage
            id="admin.users.deleteScreen.prompt"
            defaultMessage="¿Desea eliminar el usuario? Esta acción no se puede deshacer."
            values={{
              email: resource.email,
            }}
          />
        ),
        successMessage: () => (
          <FormattedMessage
            id="admin.users.notifications.deleted"
            defaultMessage="Usuario eliminado"
          />
        ),
        redirectTo: '/admin/users',
      }}
    >
      {(user: UserWithGroup) => {
        const permissionsEnabled = user.role === UserRole.CLIENT_USER;

        const tabs = [
          {
            value: TabKey.INFO,
            label: (
              <FormattedMessage
                id="admin.users.viewScreen.basicInfoTab"
                defaultMessage="Información"
              />
            ),
            content: <UserInfo user={user} canEdit />,
          },
          {
            value: TabKey.PERMISSIONS,
            label: (
              <FormattedMessage
                id="admin.users.viewScreen.stationsPermissionsTab"
                defaultMessage="Acceso a emisoras"
              />
            ),
            content: <UserPermissionsManager user={user} />,
          },
        ];

        if (permissionsEnabled) {
          tabs.push({
            value: TabKey.ACTIONS_PERMISSIONS,
            label: (
              <FormattedMessage
                id="admin.users.viewScreen.actionsPermissionsTab"
                defaultMessage="Permisos"
              />
            ),
            content: <UserActionPermissionsView user={user} />,
          });
        }

        return <ParamDrivenTabsMui id="user-view-tabs" tabs={tabs} />;
      }}
    </ResourceViewPage>
  );
}
